<script setup>
import { onMounted, onBeforeUnmount, watch } from "vue";

// Input flag to allow parent to open the modal
const props = defineProps({
  value: Boolean,
  noMaxWidth: Boolean,
  fullwidth: Boolean,
  confirmClose: Boolean,
  confirmMessage: String,
  needToConfirm: {
    type: Boolean,
    default: false,
  },
  // Modal can only be closed by parent
  needToAccept: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['input', 'update:value', 'close']);
const OVERFLOW_HIDDEN_CLASS = "tw-overflow-hidden";

// Let parent know that the modal was closed
function handleClose() {
  if (props.confirmMessage && props.needToConfirm && !window.confirm(props.confirmMessage)) {
    return;
  }

  emit('input', false);
  emit("update:value", false);
  emit('close');
}

// Close the modal when the background is clicked
function handleClick(event) {
  if (props.needToAccept) {
    return;
  }

  if (event.target.classList.contains('stk-modal-background')) {
    handleClose();
  }
}

function handleEscape(event) {
  if (props.needToAccept) {
    return;
  }

  if (event.key === "Escape") {
    handleClose();
  }
};

onMounted(() => {
  window.addEventListener("keydown", handleEscape);
  console.log("StakeModal mounted with value:", props.value);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleEscape);
  document.documentElement.classList.remove(OVERFLOW_HIDDEN_CLASS);
});

watch(
  () => props.value,
  (isOpen) => {
    console.log("StakeModal value changed to:", isOpen);
    if (isOpen) {
      document.documentElement.classList.add(OVERFLOW_HIDDEN_CLASS);
    } else {
      document.documentElement.classList.remove(OVERFLOW_HIDDEN_CLASS);
    }
  }, 
  { immediate: true }
);
</script>

<template>
  <!-- Background -->
  <div
    v-if="props.value"
    class="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-gray-800 tw-bg-opacity-70 stk-modal-background background "
    @click.self="handleClick"
  >
    <!-- Window -->
    <div class="tw-relative tw-bg-white tw-p-5 tw-rounded-lg tw-shadow-lg window" :class="{ 'no-max-width': noMaxWidth, 'is-fullwidth': fullwidth }">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.background {
  overflow-y: auto;
  padding: 3rem;
  z-index: 100;
}

.window {
  margin: auto;
  max-width: 40rem;
  overflow-x: hidden;
  overflow-y: hidden;

  &.is-fullwidth {
    width: 100%;
  }
}

.no-max-width {
  max-width: none;
}
</style>
